import { rmServerInstance } from "./api";

const cloudfrontDomain = process.env.REACT_APP_CLOUDFRONT_DOMAIN;
// Fallback when env is missing at build time (e.g. production deploy without env) so S3 URLs still convert to CloudFront
const FALLBACK_CLOUDFRONT_DOMAIN = "cdn.ravensoftware.io";

/**
 * When true, use per-URL signing (sign-url) for images instead of relying on signed cookies.
 * Use this on localhost because the browser won't set cookies for cdn.ravensoftware.io when
 * the Set-Cookie response comes from localhost. Set REACT_APP_USE_SIGNED_URLS_FOR_IMAGES=1
 * to force this in any environment (e.g. to test sign-url without cookies).
 */
export function shouldUseSignedUrlsForImages() {
  if (
    process.env.REACT_APP_USE_SIGNED_URLS_FOR_IMAGES === "true" ||
    process.env.REACT_APP_USE_SIGNED_URLS_FOR_IMAGES === "1"
  ) {
    return true;
  }
  if (
    typeof window !== "undefined" &&
    window.location?.hostname === "localhost"
  ) {
    return true;
  }
  return false;
}

/**
 * Normalizes CloudFront domain by removing protocol if present
 */
function normalizeCloudFrontDomain(domain) {
  if (!domain) return domain;
  // Remove http:// or https:// if present
  return domain.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

/**
 * Resolve CloudFront domain: use env var, or fallback for known S3 URLs so production works even if env wasn't set at build.
 */
function getEffectiveCloudFrontDomain(pathOrUrl) {
  if (cloudfrontDomain) return normalizeCloudFrontDomain(cloudfrontDomain);
  // When env is missing, still convert S3 URLs so production images work (e.g. raven-software-api.s3.amazonaws.com -> cdn.ravensoftware.io)
  if (
    pathOrUrl &&
    typeof pathOrUrl === "string" &&
    pathOrUrl.includes("s3.amazonaws.com")
  ) {
    return FALLBACK_CLOUDFRONT_DOMAIN.replace(/^https?:\/\//, "").replace(
      /\/$/,
      "",
    );
  }
  return null;
}

/**
 * Converts S3 URL to CloudFront URL
 * Handles both full S3 URLs and relative paths.
 * Uses REACT_APP_CLOUDFRONT_DOMAIN, or fallback for S3 URLs so production works when env is missing.
 */
export function s3ToCloudFront(pathOrUrl) {
  if (!pathOrUrl) return pathOrUrl;

  const effectiveDomain = getEffectiveCloudFrontDomain(pathOrUrl);
  if (!effectiveDomain) return pathOrUrl;

  const cloudfrontBaseUrl = `https://${effectiveDomain}`;

  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    try {
      const u = new URL(pathOrUrl);
      const urlHostname = u.hostname.toLowerCase();
      const domainHostname = effectiveDomain.toLowerCase();
      if (
        urlHostname.includes("cloudfront.net") ||
        urlHostname === domainHostname
      ) {
        return pathOrUrl;
      }
      const path = u.pathname + (u.search || "");
      return `${cloudfrontBaseUrl}${path}`;
    } catch (e) {
      const cleanPath = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
      return `${cloudfrontBaseUrl}${cleanPath}`;
    }
  }
  const cleanPath = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${cloudfrontBaseUrl}${cleanPath}`;
}

/**
 * Strips signing query params (?Expires=...&Key-Pair-Id=...&Signature=...) from a URL.
 * Use the result for database storage; use getSignedImageUrl() when displaying.
 */
export function stripSignedUrlParams(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    return url;
  }
}

/**
 * Ask the backend to issue CloudFront signed cookies.
 *
 * The backend will attach Set-Cookie headers for:
 * - CloudFront-Policy
 * - CloudFront-Signature
 * - CloudFront-Key-Pair-Id
 *
 * Once set, any plain CloudFront URL under the configured domain will be
 * accessible without per-URL signing.
 */
export async function ensureCloudFrontSignedCookies(expirationMinutes = 60) {
  try {
    await rmServerInstance.post("/upload/sign-cookies", {
      expiration_minutes: expirationMinutes,
    });
  } catch (error) {
    throw error;
  }
}

/**
 * Extracts the file key (path without origin) from a full URL for the sign-url API.
 * e.g. "https://cdn.ravensoftware.io/uploads/abc.png" -> "uploads/abc.png"
 */
function urlToFileKey(urlOrPath) {
  if (!urlOrPath) return null;
  try {
    const s = urlOrPath.trim();
    if (s.startsWith("http://") || s.startsWith("https://")) {
      const u = new URL(s);
      return u.pathname.replace(/^\//, "");
    }
    return s.replace(/^\//, "");
  } catch {
    return null;
  }
}

/**
 * Gets a signed CloudFront URL for a database-stored URL
 * Works with both old S3 URLs and new CloudFront URLs
 * @param {string} dbValue - The URL stored in the database (can be S3 or CloudFront URL)
 * @param {number} expirationMinutes - Expiration time in minutes (default: 5)
 * @returns {Promise<string>} Signed CloudFront URL
 */
export async function getSignedImageUrl(dbValue, expirationMinutes = 5) {
  if (!dbValue) {
    return dbValue;
  }

  // Convert S3 URL to CloudFront URL if needed (uses env or fallback for S3 URLs)
  const cloudfrontUrl = s3ToCloudFront(dbValue);
  const key = urlToFileKey(cloudfrontUrl);
  if (!key) return cloudfrontUrl;

  try {
    const response = await rmServerInstance.post("/upload/sign-url", {
      key,
      expiration_minutes: expirationMinutes,
    });

    const data = response.data;
    const signedUrl = data.signed_url ?? data.signedUrl;
    if ((data.success || data.status) && signedUrl) {
      return signedUrl;
    }
    return cloudfrontUrl;
  } catch {
    return cloudfrontUrl;
  }
}

/**
 * Upload a file via the upload server. The backend uploads to S3 and returns the URL.
 * Returns [url, content_type] for storage in the database; URL is signed when displayed via getSignedImageUrl().
 */
const s3Upload = async (selectedFile) => {
  const formData = new FormData();
  formData.append("file", selectedFile);

  const response = await rmServerInstance.post("/upload/single", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  const data = response.data;

  // New API format: { success, message, file: { fileUrl, signedUrl, key, contentType, ... } }
  if (data.success && data.file) {
    const rawUrl = data.file.fileUrl ?? data.file.signedUrl ?? data.file.url;
    const contentType =
      data.file.contentType ?? data.file.content_type ?? selectedFile.type;
    if (rawUrl) {
      const urlForStorage = stripSignedUrlParams(rawUrl) ?? rawUrl;
      return [urlForStorage, contentType];
    }
  }

  // Legacy format: { status, url, content_type }
  if (data.status && data.url) {
    const urlForStorage = stripSignedUrlParams(data.url) ?? data.url;
    return [urlForStorage, data.content_type ?? selectedFile.type];
  }

  throw new Error(data.message || "Upload failed");
};

export default s3Upload;
